/**
 * Live worker session controller (human desktop observe → subagent learn).
 */

import type { GatewayBrowserClient } from "../gateway.ts";

export type LiveSessionRow = {
  sessionId: string;
  targetEmployeeId: string;
  observerAgentId?: string;
  status: string;
  phase: string;
  skillCount: number;
  startedAt: string;
  lastActivityAt: string;
  durationMinutes?: number;
  desktopEndpoint?: string;
  recentSkills?: string[];
  observerBound?: boolean;
  humanPresent?: boolean;
  focusedWindow?: string | null;
  windowCount?: number;
  observationMode?: "full" | "sampled";
  sampleIntervalMs?: number;
  takeoverPerformed?: boolean;
};

export type LiveSessionDetail = LiveSessionRow & {
  skills?: Array<{
    skillId: string;
    name: string;
    category: string;
    confidence?: number;
    lastObserved?: string;
  }>;
  config?: {
    desktopControllerUrl?: string;
    observationMode?: string;
    sampleIntervalMs?: number;
  };
  observerSubagent?: {
    childSessionKey: string;
    runId: string;
    observerAgentId: string;
    boundAt?: string;
  } | null;
  metadata?: Record<string, unknown>;
};

export type LiveSessionsState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  liveSessionsLoading: boolean;
  liveSessionsList: LiveSessionRow[];
  liveSessionsError: string | null;
  liveSessionsSelectedId?: string | null;
  liveSessionsDetail?: LiveSessionDetail | null;
  liveSessionsDetailLoading?: boolean;
};

export type LiveSessionCreateInput = {
  targetEmployeeId: string;
  observerAgentId: string;
  desktopControllerUrl?: string;
  sampleIntervalMs?: number;
};

export async function createLiveSession(
  state: LiveSessionsState,
  input: LiveSessionCreateInput,
): Promise<string | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  state.liveSessionsError = null;
  try {
    const res = await state.client.request<{ session: { sessionId: string } }>(
      "liveSessions.create",
      {
        targetEmployeeId: input.targetEmployeeId,
        observerAgentId: input.observerAgentId,
        config: {
          observationMode: "sampled",
          sampleIntervalMs: input.sampleIntervalMs ?? 500,
          ...(input.desktopControllerUrl?.trim()
            ? { desktopControllerUrl: input.desktopControllerUrl.trim() }
            : {}),
        },
      },
    );
    await loadLiveSessions(state);
    return res.session?.sessionId ?? null;
  } catch (err) {
    state.liveSessionsError = err instanceof Error ? err.message : String(err);
    return null;
  }
}

export async function startLiveSession(
  state: LiveSessionsState,
  sessionId: string,
): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  state.liveSessionsError = null;
  try {
    await state.client.request("liveSessions.start", { sessionId });
    await loadLiveSessions(state);
    return true;
  } catch (err) {
    state.liveSessionsError = err instanceof Error ? err.message : String(err);
    return false;
  }
}

export async function pauseLiveSession(
  state: LiveSessionsState,
  sessionId: string,
): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  state.liveSessionsError = null;
  try {
    await state.client.request("liveSessions.pause", { sessionId });
    await loadLiveSessions(state);
    return true;
  } catch (err) {
    state.liveSessionsError = err instanceof Error ? err.message : String(err);
    return false;
  }
}

export async function resumeLiveSession(
  state: LiveSessionsState,
  sessionId: string,
): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  state.liveSessionsError = null;
  try {
    await state.client.request("liveSessions.resume", { sessionId });
    await loadLiveSessions(state);
    return true;
  } catch (err) {
    state.liveSessionsError = err instanceof Error ? err.message : String(err);
    return false;
  }
}

export async function endLiveSession(
  state: LiveSessionsState,
  sessionId: string,
): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }
  state.liveSessionsError = null;
  try {
    await state.client.request("liveSessions.end", { sessionId });
    if (state.liveSessionsSelectedId === sessionId) {
      state.liveSessionsSelectedId = null;
      state.liveSessionsDetail = null;
    }
    await loadLiveSessions(state);
    return true;
  } catch (err) {
    state.liveSessionsError = err instanceof Error ? err.message : String(err);
    return false;
  }
}

export async function loadLiveSessions(state: LiveSessionsState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  state.liveSessionsLoading = true;
  state.liveSessionsError = null;
  try {
    const res = await state.client.request<{
      sessions: LiveSessionRow[];
      total?: number;
      appliedLimit?: number;
      truncated?: boolean;
    }>("liveSessions.list", { limit: 30 });
    state.liveSessionsList = res.sessions ?? [];
    const selectedId = state.liveSessionsSelectedId;
    if (selectedId && state.liveSessionsList.some((s) => s.sessionId === selectedId)) {
      await loadLiveSessionDetail(state, selectedId, { quiet: true });
    } else if (selectedId) {
      state.liveSessionsSelectedId = null;
      state.liveSessionsDetail = null;
    }
  } catch (err) {
    state.liveSessionsError = err instanceof Error ? err.message : String(err);
    state.liveSessionsList = [];
  } finally {
    state.liveSessionsLoading = false;
  }
}

export async function loadLiveSessionDetail(
  state: LiveSessionsState,
  sessionId: string,
  opts?: { quiet?: boolean },
): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  if (!opts?.quiet) {
    state.liveSessionsDetailLoading = true;
  }
  try {
    const res = await state.client.request<{
      session: LiveSessionDetail & {
        config?: LiveSessionDetail["config"];
        skills?: LiveSessionDetail["skills"];
      };
      observerSubagent?: LiveSessionDetail["observerSubagent"];
    }>("liveSessions.get", { sessionId });
    const session = res.session;
    state.liveSessionsDetail = {
      ...session,
      observerSubagent: res.observerSubagent ?? session.observerSubagent ?? null,
      skills: session.skills,
      config: session.config,
    };
    state.liveSessionsSelectedId = sessionId;
  } catch (err) {
    if (!opts?.quiet) {
      state.liveSessionsError = err instanceof Error ? err.message : String(err);
    }
  } finally {
    state.liveSessionsDetailLoading = false;
  }
}

export function selectLiveSession(state: LiveSessionsState, sessionId: string | null): void {
  state.liveSessionsSelectedId = sessionId;
  if (!sessionId) {
    state.liveSessionsDetail = null;
    return;
  }
  const row = state.liveSessionsList.find((s) => s.sessionId === sessionId);
  if (row) {
    state.liveSessionsDetail = { ...row };
  }
  void loadLiveSessionDetail(state, sessionId);
}
